/**
 * ShareStory — user-facing form for opting into the public testimonial
 * carousel. Only reachable to users whose oldest match is ≥ 30 days old
 * (backend enforces via /testimonials/eligible).
 *
 * Ships as `/share-story`. Behind the ProtectedRoute so anonymous visitors
 * cannot spam submissions.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { Loader2, Camera, Heart, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import BackNavButton from '@/components/BackNavButton';

const STATUS_OPTIONS = [
  { value: 'dating',    label: 'Dating' },
  { value: 'exclusive', label: 'Exclusive' },
  { value: 'engaged',   label: 'Engaged' },
  { value: 'married',   label: 'Married' },
];

const QUOTE_MIN = 30;
const QUOTE_MAX = 280;

export default function ShareStory() {
  const navigate = useNavigate();
  const [eligibility, setEligibility] = useState(null);
  const [existing, setExisting] = useState(null);
  const [displayNames, setDisplayNames] = useState('');
  const [quote, setQuote] = useState('');
  const [status, setStatus] = useState('dating');
  const [partnerConsent, setPartnerConsent] = useState(false);
  const [photoUrl, setPhotoUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [elig, mine] = await Promise.all([
          api.get('/testimonials/eligible'),
          api.get('/testimonials/my'),
        ]);
        if (cancelled) return;
        setEligibility(elig.data);
        const active = (mine.data.items || []).find((t) => t.status !== 'rejected');
        setExisting(active || null);
      } catch (e) {
        if (!cancelled) toast.error('Could not load your story status');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const onPickPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Image must be under 8MB'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPhotoUrl(res.data.url || res.data.path);
      toast.success('Photo uploaded');
    } catch {
      toast.error('Photo upload failed');
    } finally {
      setUploading(false);
    }
  };

  const canSubmit =
    displayNames.trim().length >= 2 &&
    quote.trim().length >= QUOTE_MIN &&
    quote.trim().length <= QUOTE_MAX &&
    partnerConsent &&
    !submitting;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post('/testimonials', {
        display_names: displayNames.trim(),
        quote: quote.trim(),
        photo_url: photoUrl || null,
        relationship_status: status,
        partner_consent: partnerConsent,
      });
      toast.success('Story submitted for review — thank you!');
      navigate('/matches');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#B91C1C]" /></div>;
  }

  return (
    <div className="min-h-screen bg-white pb-24" data-testid="share-story-page">
      <div className="px-5 pt-6 pb-3 flex items-center gap-3">
        <BackNavButton />
        <h1 className="font-brand text-2xl font-[600] tracking-tight">Share your story</h1>
      </div>

      {existing ? (
        <div className="px-5 mt-6">
          <div className="rounded-2xl border border-emerald-100 bg-emerald-50/60 p-5 flex gap-3" data-testid="share-existing-status">
            <CheckCircle2 className="text-emerald-600 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="text-sm font-semibold text-emerald-900">
                {existing.status === 'approved' ? 'Your story is live on the homepage.' : 'Your story is under review.'}
              </p>
              <p className="text-xs text-emerald-800/80 mt-1">
                Attribution: <span className="font-medium">{existing.display_names}</span>
                {existing.approved_at && ` · approved ${new Date(existing.approved_at).toLocaleDateString()}`}
              </p>
              <p className="text-xs text-emerald-800/80 mt-2 italic">&ldquo;{existing.quote}&rdquo;</p>
            </div>
          </div>
        </div>
      ) : !eligibility?.eligible ? (
        <div className="px-5 mt-6">
          <div className="rounded-2xl border border-[var(--hairline)] bg-white p-5" data-testid="share-not-eligible">
            <Heart className="text-[#B91C1C] mb-3" size={22} />
            <h2 className="text-base font-semibold mb-2">Story submissions open at {eligibility?.min_days ?? 30} days.</h2>
            <p className="text-sm text-zinc-600 leading-relaxed">
              We only feature couples once you&rsquo;ve had time to actually build something together.
              {' '}Come back after you&rsquo;ve been matched with someone for at least a month.
            </p>
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="px-5 mt-4 space-y-5" data-testid="share-story-form">
          <p className="text-sm text-zinc-600 leading-relaxed">
            Tell future Meet to Keep users what happened after you matched.
            Our team reviews every submission — usually within 1-2 business days.
          </p>

          {/* Photo */}
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Photo of you two (optional)</label>
            <div className="mt-2 flex items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-black/[0.04] overflow-hidden flex items-center justify-center flex-shrink-0">
                {photoUrl
                  ? <img src={photoUrl} alt="Story preview" className="w-full h-full object-cover" data-testid="share-photo-preview" />
                  : <Camera className="text-zinc-400" size={22} />}
              </div>
              <label className="text-sm font-semibold text-[#B91C1C] cursor-pointer" data-testid="share-photo-picker">
                {uploading ? 'Uploading…' : (photoUrl ? 'Replace photo' : 'Upload a photo')}
                <input type="file" accept="image/*" className="hidden" onChange={onPickPhoto} disabled={uploading} />
              </label>
            </div>
            <p className="text-[11px] text-zinc-500 mt-2">Leave blank to use your profile photo instead.</p>
          </div>

          {/* Display names */}
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">How to credit you</label>
            <input
              value={displayNames}
              onChange={(e) => setDisplayNames(e.target.value)}
              maxLength={60}
              placeholder="Alex & Jamie"
              className="mt-2 w-full h-11 px-3 rounded-xl border border-[var(--hairline)] focus:border-black text-base bg-white"
              data-testid="share-display-names"
            />
            <p className="text-[11px] text-zinc-500 mt-1">First names only. No last names.</p>
          </div>

          {/* Quote */}
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Your story</label>
            <textarea
              value={quote}
              onChange={(e) => setQuote(e.target.value.slice(0, QUOTE_MAX))}
              rows={4}
              placeholder="What happened after you matched?"
              className="mt-2 w-full px-3 py-2.5 rounded-xl border border-[var(--hairline)] focus:border-black text-base bg-white resize-y"
              data-testid="share-quote"
            />
            <div className="flex justify-between mt-1">
              <p className="text-[11px] text-zinc-500">{quote.length < QUOTE_MIN ? `${QUOTE_MIN - quote.length} more characters` : ' '}</p>
              <p className="text-[11px] text-zinc-400 tabular-nums">{quote.length}/{QUOTE_MAX}</p>
            </div>
          </div>

          {/* Relationship status */}
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Where you&rsquo;re at now</label>
            <div className="mt-2 flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setStatus(s.value)}
                  data-testid={`share-status-${s.value}`}
                  className={`px-3.5 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                    status === s.value ? 'bg-[#0F0F10] text-white border-[#0F0F10]' : 'bg-white text-[#0F0F10] border-[var(--hairline)] hover:border-black/[0.25]'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Consent */}
          <label className="flex items-start gap-3 cursor-pointer" data-testid="share-consent-label">
            <input
              type="checkbox"
              checked={partnerConsent}
              onChange={(e) => setPartnerConsent(e.target.checked)}
              className="mt-1 w-4 h-4 accent-[#B91C1C]"
              data-testid="share-consent-checkbox"
            />
            <span className="text-sm text-zinc-700 leading-relaxed">
              My partner has agreed to being featured, including their first name and this photo.
            </span>
          </label>

          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="share-submit-btn"
            className={`w-full h-12 rounded-full text-sm font-semibold transition-all ${
              canSubmit ? 'bg-[#0F0F10] text-white hover:bg-black' : 'bg-black/[0.06] text-black/40 cursor-not-allowed'
            }`}
          >
            {submitting ? 'Submitting…' : 'Submit for review'}
          </button>
          <p className="text-[11px] text-zinc-500 text-center leading-relaxed">
            By submitting, you allow Meet to Keep to display your story on the homepage.
            You can request removal at any time by emailing hello@meettokeep.com.
          </p>
        </form>
      )}
    </div>
  );
}
